import React, {
    ChangeEvent,
    useState
} from 'react';

import {
    Box,
    Button,
    Typography,
    Divider,
    Container,
    TextField,
    MenuItem
} from '@mui/material';

import {
    CloudUpload,
    Movie,
    Image as ImageIcon,
    Send
} from '@mui/icons-material';

interface MediaUploaderProps {

    preview: string | null;

    fileType: string | null;

    onImageChange: (
        e: ChangeEvent<HTMLInputElement>
    ) => void;

    onClearMedia: () => void;

    onSubmitMedia?: (
        payload: {
            mediaUrl: string | null;
            lifetimeMinutes: number | null;
        }
    ) => void;

    poppins: {
        fontFamily: string;
    };

    isUploadingMedia: boolean;
}

const MediaUploader: React.FC<
    MediaUploaderProps
> = ({
         preview,
         fileType,
         onImageChange,
         onClearMedia,
         onSubmitMedia,
         isUploadingMedia,
         poppins
     }) => {

    // =========================
    // REDIRECT URL STATE
    // =========================

    const [
        externalUrl,
        setExternalUrl
    ] = useState<string>('');

    // =========================
    // LIFETIME STATE
    // =========================

    const [
        lifetime,
        setLifetime
    ] = useState<number | string>(
        'infinite'
    );

    // =========================
    // DETECT VIDEO
    // =========================

    const isVideo =
        fileType?.startsWith('video/') ||
        externalUrl.match(
            /\.(mp4|webm|mov)($|\?)/i
        );

    // =========================
    // SUBMIT
    // =========================

    const handleFormSubmit = () => {

        if (onSubmitMedia) {

            const minutes =
                lifetime === 'infinite'
                    ? null
                    : Number(lifetime);

            onSubmitMedia({

                // REDIRECT URL
                mediaUrl:
                    externalUrl.trim() || null,

                // EXPIRY
                lifetimeMinutes: minutes
            });
        }
    };

    return (

        <Container
            maxWidth="lg"
            sx={{
                borderRadius: '10px',
                boxShadow:
                    '0 10px 40px rgba(0,0,0,0.03)',
                mb: 10,
                pt: 5,
                pb: 20,
                bgcolor: 'white'
            }}
        >

            {/* =========================
                MAIN BOX
            ========================= */}

            <Box
                sx={{
                    border:
                        '2px dashed #E0E5F2',

                    borderRadius: '20px',

                    height: '100%',

                    display: 'flex',

                    flexDirection: 'column',

                    alignItems: 'center',

                    justifyContent: 'center',

                    p: 3,

                    bgcolor: '#F4F7FE',

                    minHeight: 280,

                    transition:
                        'all 0.3s ease',

                    '&:hover': {
                        borderColor: 'orange',
                        bgcolor: '#FFF'
                    }
                }}
            >

                {/* =========================
                    FILE UPLOADER
                ========================= */}

                {!preview ? (

                    <Button
                        variant="text"
                        component="label"
                        startIcon={<CloudUpload />}
                        sx={{
                            ...poppins,
                            textTransform: 'none',
                            color: '#707EAE',
                            fontWeight: 600,
                            flexDirection: 'column',
                            gap: 1
                        }}
                    >

                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600
                            }}
                        >
                            Upload Media Asset
                        </Typography>

                        <Typography
                            variant="caption"
                            sx={{
                                color: '#A3AED0'
                            }}
                        >
                            (Images, GIFs, or Videos)
                        </Typography>

                        <input
                            type="file"
                            hidden
                            accept="image/*,video/*"
                            onChange={onImageChange}
                        />
                    </Button>

                ) : (

                    <Box
                        textAlign="center"
                        sx={{
                            width: '100%'
                        }}
                    >

                        {/* =========================
                            MEDIA PREVIEW
                        ========================= */}

                        <Box sx={{ mb: 2 }}>

                            {isVideo ? (

                                <video
                                    src={preview}
                                    controls
                                    style={{
                                        width: '100%',
                                        maxHeight: 350,
                                        borderRadius:
                                            '16px',
                                        objectFit:
                                            'contain',
                                        backgroundColor:
                                            '#000'
                                    }}
                                />

                            ) : (

                                <img
                                    src={preview}
                                    alt="preview"
                                    style={{
                                        width: '100%',
                                        maxHeight: 220,
                                        borderRadius:
                                            '16px',
                                        objectFit:
                                            'contain'
                                    }}
                                />
                            )}
                        </Box>

                        {/* =========================
                            REMOVE BUTTON
                        ========================= */}

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent:
                                    'center',
                                gap: 1,
                                mb: 1
                            }}
                        >

                            {isVideo ? (
                                <Movie
                                    fontSize="small"
                                    color="action"
                                />
                            ) : (
                                <ImageIcon
                                    fontSize="small"
                                    color="action"
                                />
                            )}

                            <Button
                                size="small"
                                color="error"
                                variant="text"
                                onClick={
                                    onClearMedia
                                }
                                sx={{
                                    fontWeight: 600,
                                    ...poppins
                                }}
                            >
                                Remove Media
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* =========================
                    REDIRECT URL FIELD
                ========================= */}

                <Box
                    sx={{
                        width: '100%',
                        mt: 3
                    }}
                >

                    <TextField
                        fullWidth
                        label="Redirect Website URL"
                        variant="outlined"
                        value={externalUrl}
                        onChange={(e) =>
                            setExternalUrl(
                                e.target.value
                            )
                        }
                        placeholder="https://youtube.com/..."
                        InputProps={{
                            sx: {
                                borderRadius:
                                    '12px',
                                bgcolor: 'white',
                                ...poppins
                            }
                        }}
                    />
                </Box>

                {/* =========================
                    SHOW SETTINGS IF:
                    FILE OR URL EXISTS
                ========================= */}

                {(preview || externalUrl) && (

                    <Box
                        sx={{
                            width: '100%',
                            mt: 2
                        }}
                    >

                        <Divider
                            sx={{
                                my: 2,
                                borderStyle:
                                    'dashed'
                            }}
                        />

                        {/* =========================
                            LIFETIME
                        ========================= */}

                        <TextField
                            select
                            fullWidth
                            label="Online Retention Lifetime Window"
                            value={lifetime}
                            onChange={(e) =>
                                setLifetime(
                                    e.target.value
                                )
                            }
                            InputProps={{
                                sx: {
                                    borderRadius:
                                        '12px',
                                    bgcolor:
                                        'white',
                                    ...poppins
                                }
                            }}
                            sx={{ mb: 3 }}
                        >

                            <MenuItem value="infinite">
                                Keep Online
                                Indefinitely
                            </MenuItem>

                            <MenuItem value={15}>
                                15 Minutes
                            </MenuItem>

                            <MenuItem value={60}>
                                1 Hour
                            </MenuItem>

                            <MenuItem value={1440}>
                                24 Hours
                            </MenuItem>

                            <MenuItem value={10080}>
                                7 Days
                            </MenuItem>

                        </TextField>

                        {/* =========================
                            SUBMIT BUTTON
                        ========================= */}

                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<Send />}
                            onClick={
                                handleFormSubmit
                            }
                            sx={{
                                bgcolor: 'orange',

                                color: 'white',

                                borderRadius:
                                    '12px',

                                textTransform:
                                    'none',

                                fontWeight: 700,

                                py: 1.5,

                                fontSize:
                                    '0.95rem',

                                '&:hover': {
                                    bgcolor:
                                        '#E28700'
                                },

                                ...poppins
                            }}
                        >

                            {isUploadingMedia
                                ? "Uploading Advert..."
                                : "Upload Advert"}

                        </Button>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default MediaUploader;